import React from "react";
import { Box, Button } from "@mui/material";
import { updateIn } from "shared/src/util.mjs";
import ButtonLoader from "./ButtonLoader";
import { errorsFromApi } from "../form";
import { useSnackbarMessage } from "./Snackbar";
import { useTranslation } from "react-i18next";
import * as api from "../services/api";
import * as logging from "../logging";

function initialExportState() {
  return { active: false, selected: {} };
}

function setLoading(state, loading) {
  return { ...state, loading };
}

function toggleActive(state) {
  return { active: !state.active, selected: {} };
}

export function toggleSelection(state, file) {
  return {
    ...state,
    selected: updateIn(
      state.selected,
      [file.type, file.objectid, file.file],
      (prev) => !prev,
    ),
  };
}

function getSelectedCount(state) {
  return Object.values(state.selected)
    .flatMap((v) => Object.values(v))
    .flatMap((v) => Object.values(v))
    .filter((v) => v).length;
}

function getFiles(state) {
  return Object.entries(state.selected).flatMap(([type, rest]) =>
    Object.entries(rest).flatMap(([objectid, rest]) =>
      Object.keys(rest).map((file) => ({
        objectid,
        type: parseInt(type, 10),
        file: parseInt(file, 10),
      })),
    ),
  );
}

export function isSelected(state, file) {
  return state.selected?.[file.type]?.[file.objectid]?.[file.file] === true;
}

export function useExportState() {
  return React.useState(initialExportState());
}

export function Selectable({ children, onSelect, selected, enabled }) {
  const shadow = selected
    ? "0px 4px 4px 0px color-mix(in srgb, var(--mui-palette-primary-main), transparent 50%)"
    : "0px 4px 4px 0px color-mix(in srgb, var(--mui-palette-text-primary), transparent 50%)";

  return (
    <Box
      sx={{
        background: "none",
        borderColor: selected ? "primary.main" : "transparent",
        borderWidth: 1,
        borderStyle: "solid",
        "&:hover": enabled
          ? {
              cursor: "pointer",
              boxShadow: shadow,
            }
          : {},
      }}
      onClick={enabled ? () => onSelect() : null}
    >
      {children}
    </Box>
  );
}

function SelectFilesButton({ setExportState }) {
  return (
    <Button
      variant="outlined"
      onClick={() => setExportState((state) => toggleActive(state))}
    >
      Vybrat soubory pro export
    </Button>
  );
}

function ExportSelectedFilesButton({ client_id, exportState, setExportState }) {
  const count = getSelectedCount(exportState);
  const { t } = useTranslation();
  const setSnackbarMessage = useSnackbarMessage();

  return (
    <Button
      disabled={count === 0 || exportState.loading}
      variant="outlined"
      onClick={async () => {
        try {
          setExportState((state) => setLoading(state, true));
          const result = await api.exportFiles({
            client_id,
            files: getFiles(exportState),
          });
          const errors = errorsFromApi({ t, result });
          if (errors.length === 0) {
            setExportState((state) => toggleActive(state));
            setSnackbarMessage({ content: "Soubory poslány na export." });
          } else {
            setSnackbarMessage({
              content: "Nepovedlo se exportovat soubory.",
              severity: "error",
            });
          }
        } catch (e) {
          logging.error(e);
          setSnackbarMessage({
            content: "Nepovedlo se exportovat soubory.",
            severity: "error",
          });
        } finally {
          setExportState((state) => setLoading(state, false));
        }
      }}
    >
      {exportState.loading && <ButtonLoader />}
      Exportovat ({count})
    </Button>
  );
}

export function ExportButton({ client_id, exportState, setExportState }) {
  return exportState.active ? (
    <ExportSelectedFilesButton
      client_id={client_id}
      exportState={exportState}
      setExportState={setExportState}
    />
  ) : (
    <SelectFilesButton
      exportState={exportState}
      setExportState={setExportState}
    />
  );
}
